import { Grid, Typography, TextField } from '@material-ui/core'
import React, { ChangeEvent, Dispatch } from 'react'
import { useTranslation } from 'react-i18next'
import { s, MainSection } from './style'
import { Documents } from './Documents'
import { formatCents } from '../../../Utilities'
import { GoogleMap, Marker, withGoogleMap } from 'react-google-maps'
import ReactMarkdown from 'react-markdown'
import { getAddress } from '../../../Graphql-Utilities'
import { ProposalInfoCardProposalFragment } from './ProposalInfoCard.generated-types'
import FacilityList from '../FacilitiesList/FacilitiesList'
import { Sustainability } from './Sustainability'
import { BusIcon } from '../../../Components/Icons/Icons'
import { TotalBlock } from '../DetailsCard/CostBlock'
import { Chip, ChipProps } from '@mui/material'
import Description from './Description'

interface MapProps {
  loadingElement: any
  containerElement: any
  mapElement: any
  lat: number
  lon: number
}

const LocationChip = ({
  location,
  size = 'small',
  ...rest
}: { location: string, size?: 'small' | 'medium' } & Omit<
ChipProps,
'size'
>): JSX.Element => (
  <Chip
    size={size}
    icon={<BusIcon style={{ marginLeft: 0 }} titleAccess='Nearest station' fontSize='small' />}
    label={location}
    {...rest}
  />
)

const Map: React.ComponentClass<MapProps> = withGoogleMap(({ lat, lon }): JSX.Element => {
  const coords = {
    lat: lat,
    lng: lon,
  }

  return (
    <GoogleMap
      center={coords}
      defaultZoom={16}
      defaultOptions={{
        fullscreenControl: true,
        mapTypeControl: true,
        streetViewControl: false,
      }}>
      <Marker position={coords} />
    </GoogleMap>
  )
})

interface ProposalInfoCardProps {
  proposal: ProposalInfoCardProposalFragment
  editing: boolean
  setProposal: Dispatch<any>
}

export const ProposalInfoCard: React.FC<ProposalInfoCardProps> = ({
  proposal,
  editing,
  setProposal,
}) => {
  const { t } = useTranslation()
  const {
    UUID,
    space: {
      venue,
      description,
      spaceFeatures,
      spaceDocuments
    },
    message,
    currency,
    baseCostTotal,
    baseCostDescription,
    documents
  } = proposal

  const {
    nearestTransit,
    latitude,
    longitude
  } = venue

  const address = venue && getAddress(proposal)

  const facilities = []
  if (spaceFeatures?.length > 0) {
    spaceFeatures.forEach((f: any) => facilities.push(f.name))
  }

  const handleUpdateMessage = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setProposal({ ...proposal, message: event.target.value })
  }

  const handleUpdateBaseCostDescription = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setProposal({ ...proposal, baseCostDescription: event.target.value })
  }

  const handleUpdateBaseCostTotal = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const withPence = Number(event.target.value) * 100

    setProposal({
      ...proposal,
      baseCostTotal: withPence,
    })
  }

  return (
    <MainSection>
      { message?.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <Typography style={s.sectionTitle} variant="h2" gutterBottom>
              {t('proposal.messageFromVenue', 'Their message to you')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            { editing ? (
              <TextField
                variant="outlined"
                style={s.msgText}
                defaultValue={message}
                multiline
                onChange={handleUpdateMessage}
              />
            ) : (
              <Typography style={s.msgText} variant="body1" gutterBottom>
                <ReactMarkdown>{message}</ReactMarkdown>
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      <Grid container id='costBreakdown'>
        <Grid item xs={12}>
          <Typography style={s.sectionTitle} variant="h2" gutterBottom>
            {t('proposal.costBreakdown', 'Cost breakdown')}
          </Typography>
          <Typography style={s.detailTitle} variant="h3" gutterBottom>
            {t('proposal.whatsincluded', 'What’s included')}
          </Typography>
          { editing ? (
            <TextField
              variant="outlined"
              style={s.msgText}
              defaultValue={baseCostDescription}
              multiline
              onChange={handleUpdateBaseCostDescription}
            />
          ) : (
            <Typography style={s.msgText} variant="body1" gutterBottom>
              <ReactMarkdown>{baseCostDescription}</ReactMarkdown>
            </Typography>
          )}
          { editing ? (
            <Grid container style={s.editingBaseCostTotal}>
              <Typography variant="body1" style={s.baseCostTotalTitle}>
                {`Total (${currency === 'AUD' ? 'inc GST' : 'ex VAT'})`}
              </Typography>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                style={s.baseCostTotalInput}
                defaultValue={baseCostTotal / 100}
                onChange={handleUpdateBaseCostTotal}
              />
            </Grid>
          ) : (
            <TotalBlock title={`Total (${currency === 'AUD' ? 'inc GST' : 'ex VAT'})`}>
              {formatCents(currency, baseCostTotal)}
            </TotalBlock>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <Typography style={s.sectionTitle} variant="h2" gutterBottom>
            {t('proposal.space.sustainability', 'Sustainability')}
          </Typography>
          <Typography style={s.detailTitle} variant="h3" gutterBottom>
            {t('proposal.space.footprint', 'Projected event footprint')}
          </Typography>
          <Sustainability proposalUUID={UUID} eventUUID={proposal.event.UUID} />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          <Typography style={s.sectionTitle} variant="h2" gutterBottom>
            {t('proposal.space.desc', 'Space details')}
          </Typography>
          <Typography style={s.detailTitle} variant="h3" gutterBottom>
            {t('proposal.space.about', 'About the space')}
          </Typography>
          <Typography style={s.msgText} variant="body1" >
            <Description description={description} />
          </Typography>
        </Grid>

        {spaceFeatures?.length > 0 && (
          <Grid item xs={12}>
            <Typography style={s.detailTitle} variant="h3" gutterBottom>
              {t('proposal.facilities', 'Facilities')}
            </Typography>
            <FacilityList facilities={spaceFeatures} />
          </Grid>
        )}

        {(documents?.length > 0 || spaceDocuments?.length > 0) && (
          <Grid item xs={12}>
            <Typography style={s.detailTitle} variant="h3" gutterBottom>
              {t('proposal.attachments', 'Other information')}
            </Typography>
            <Documents proposal={proposal} />
          </Grid>
        )}

        {address && (
          <>
            <Grid item xs={12}>
              <Typography style={s.detailTitle} variant="h3" gutterBottom>
                {t('proposal.space.loc', 'Location')}
              </Typography>
              <Typography style={s.msgText} variant="body1" gutterBottom>
                {address}
              </Typography>
              {nearestTransit?.station && (
                <LocationChip
                  size='medium'
                  sx={{ fontFamily: 'Montserrat', backgroundColor: '#fafafa', fontSize: 16 }}
                  location={`${
                    nearestTransit.station
                  } (${nearestTransit.distanceKm.toFixed(1)} km)`}
                />
              )}
            </Grid>
          </>
        )}

        {latitude && longitude && (
          <Grid item xs={12} id="locationMap">
            <Map
              lat={latitude}
              lon={longitude}
              loadingElement={<div style={{ height: '100%' }} />}
              containerElement={<div style={{ height: 460, width: '100%', marginTop: 12 }} />}
              mapElement={<div style={{ height: '100%', width: '100%' }} />}
            />
          </Grid>
        )}
      </Grid>
    </MainSection>
  )
}
